import React from 'react';
import {Icon, Popup} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import {filter as _filter} from 'lodash';
import moment from 'moment/moment';

import PopupTable from '@appComponents/PopupTable';
import {IconWithTooltip} from '@appComponents/IconCollection';
import EventContentStreams from '@modules/liveControl/components/StreamsPlayback';
import {convertToInt} from '@utils/helpers';
import {showModal} from '@utils/modal';

/**
 * @param {boolean} isHiddenInLiveControl
 * @returns {JSX|null}
 */
export const HideInLiveControlRenderer = ({isHiddenInLiveControl}) => (
    isHiddenInLiveControl
        ? (
            <span className='eventContent__headerInfo'>
                <Icon className='eye slash outline'/> Hidden in Live Control
            </span>
        )
        : null
);

HideInLiveControlRenderer.propTypes = {
    isHiddenInLiveControl: PropTypes.bool,
};

export const Is247StreamRenderer = ({is247stream}) => {
    return (
        is247stream
            ? (
                <span className='eventContent__headerInfo'>
                    <Icon className='sync alternate' />
                        24/7
                </span>
            )
            : null
    );
};

Is247StreamRenderer.propTypes = {
    is247stream: PropTypes.bool,
};

export const MainContentRenderer = ({isMainContent}) => {
    return (
        isMainContent
            ? (
                <span className='eventContent__headerInfo'>
                    <Icon className='check'/>
                        Main content of the event
                </span>
            )
            : null
    );
};

MainContentRenderer.propTypes = {
    isMainContent: PropTypes.bool,
};

export const IsWscRenderer = ({id, md5}) => {
    return (
        (id || md5) && (
            <Popup
                className='--noPadding'
                flowing
                trigger={
                    <span className='eventContent__headerInfo'>
                            WSC
                    </span>
                }
                hoverable
            >
                <PopupTable rows={[
                    {
                        header: 'WSC video ID:',
                        body: id || '-',
                    },
                    {
                        header: 'WSC video md5:',
                        body: md5 || '-',
                    },
                ]} />
            </Popup>
        ) || null
    );
};

IsWscRenderer.propTypes = {
    id: PropTypes.string,
    md5: PropTypes.string,
};

export const getTitle = (id, contentTypeName, name) => {
    return _filter([id, contentTypeName, name]).join(' - ');
};

export const TitleRenderer = ({id, contentTypeName, name}) => {
    return (
        <span>{getTitle(id, contentTypeName, name)}</span>
    );
};

TitleRenderer.propTypes = {
    id: PropTypes.string,
    contentTypeName: PropTypes.string,
    name: PropTypes.string,
};

export const IconOutputStreamModal = ({popupProps, eventId, eventContentId}) => {
    return (
        <IconWithTooltip
            name='play circle outline'
            onClick={(e)=> openModalToOutputStream(e, eventId, eventContentId)}
            tooltip='Stream playback'
            popupProps={popupProps}
        />
    );
};

IconOutputStreamModal.propTypes = {
    popupProps: PropTypes.object,
    eventId: PropTypes.number,
    eventContentId: PropTypes.string,
};

export const openModalToOutputStream = (e, eventId, eventContentId) => {
    disableAccordionToggle(e);

    showModal({
        className: 'streamPlayback',
        isVisible: true,
        size: 'large',
        content:
            <EventContentStreams
                eventId={convertToInt(eventId)}
                eventContentId={convertToInt(eventContentId)}
            />,
    });
};

export const disableAccordionToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
};

export const ClockOrDurationRenderer = ({data}) => {
    const {
        start_datetime,
        end_datetime,
        video_duration,
        video_length,
    } = data;
    const startDateTime = start_datetime ? moment(start_datetime).format('YYYY-MM-DD HH:mm') : null;
    const endDateTime = end_datetime ? moment(end_datetime).format('YYYY-MM-DD HH:mm') : null;

    return (
        <span className='eventContent__headerInfo --align-flex-end'>
            {start_datetime && <StartEndDateInfo startDateTime={startDateTime} endDateTime={endDateTime}/>}
            {(!video_length && !video_duration) ? null :
                <VideoLengthInfo videoLength={video_length} videoDuration={video_duration}/>}
        </span>
    );
};

ClockOrDurationRenderer.propTypes = {
    data: PropTypes.shape({
        start_datetime: PropTypes.string,
        end_datetime: PropTypes.string,
        video_duration: PropTypes.number,
        video_length: PropTypes.number,
    }),
};

export const VideoLengthInfo = ({videoLength, videoDuration}) => {
    let durationText;

    if (videoLength) {
        durationText = `${videoLength}min`;
    } else if (videoDuration) {
        const videoDurationTime = moment.duration(videoDuration),
            hoursText = (0 < videoDurationTime.hours()) ? `${videoDurationTime.hours()}h ` : '',
            minutesText = (0 < videoDurationTime.minutes() || hoursText) ? `${videoDurationTime.minutes()}min ` : '';

        durationText = `${hoursText}${minutesText}${videoDurationTime.seconds()}s`;
    }

    return (
        <span className='eventContent__headerInfo --align-flex-end'>
            <Icon className='history --rotated-180deg'/>
            <span>{durationText}</span>
        </span>
    );
};

VideoLengthInfo.propTypes = {
    videoLength: PropTypes.number,
    videoDuration: PropTypes.number,
};

export const StartEndDateInfo = ({startDateTime, endDateTime}) => (
    <span className='eventContent__headerInfo'>
        <Icon className='clock'/>
        {_filter([startDateTime, endDateTime]).join(' - ')}
    </span>
);

StartEndDateInfo.propTypes = {
    startDateTime: PropTypes.string,
    endDateTime: PropTypes.string,
};
